import { format } from 'date-fns';
import { anchorSentence } from './anchors';
import {
  FANWAVE_MODE,
  GAME_DAY_SCHEDULE,
  gameDayScheduleKind,
  gameDayTimingShape,
  matchMinutesLabel,
  scheduleUnrecorded,
  WAVE_STATUS,
  WAVE_TIMING
} from './constants';

/** Row-level date formatting. Also used for the absolute edge of an anchor pair. */
export const fmt = (d, pattern = 'MMM dd, HH:mm') => (d ? format(new Date(d), pattern) : '—');

export const scheduleLine = (wave) => {
  // A wave configured with ANCHORS describes itself, so nothing below has to be inferred from
  // the legacy fields — and for several shapes it cannot be. `anchorsToLegacy` on the API keeps
  // only what those four fields can express, so a wave that opens at the second-half kick-off
  // arrives as an `endsAt` alone: identical to a Half-Time wave with a clock end. This branch is
  // why two waves on one match, one from Half-Time and one from the restart, no longer both read
  // "Half-Time → …".
  if (wave.openAnchor && wave.closeAnchor) {
    return `${anchorSentence(wave.openAnchor, fmt)} → ${anchorSentence(wave.closeAnchor, fmt)}`;
  }
  if (wave.mode !== FANWAVE_MODE.GAME_DAY) {
    return wave.startsAt && wave.endsAt ? `${fmt(wave.startsAt)} → ${fmt(wave.endsAt)}` : '—';
  }
  // A run archived before the schedule was persisted: state the times it ran and admit the
  // schedule is gone, rather than naming the fallback's guess.
  if (scheduleUnrecorded(wave)) {
    return wave.startsAt
      ? `Ran ${fmt(wave.startsAt)} → ${fmt(wave.endsAt)} · schedule not recorded`
      : 'Schedule not recorded';
  }
  const kind = gameDayScheduleKind(wave);
  if (kind === GAME_DAY_SCHEDULE.MINUTES) return `Match minutes ${matchMinutesLabel(wave)}`;
  // One edge on each clock. Named explicitly because it is the kind a three-way switch misses:
  // without this it fell through to the default Half-Time line and its real close vanished.
  if (kind === GAME_DAY_SCHEDULE.MIXED) {
    return `${wave.startMinute}' → ${fmt(wave.endsAt)}`;
  }
  // The wall-clock end without a start of its own: the wave opens at the whistle like a default
  // one and closes at an exact time — which is what a following wave starts from.
  if (gameDayTimingShape(wave) === WAVE_TIMING.HALF_TIME_UNTIL) {
    return `Half-Time → ${fmt(wave.endsAt)}`;
  }
  if (kind === GAME_DAY_SCHEDULE.WINDOW) {
    return `Clock window ${fmt(wave.startsAt)} → ${fmt(wave.endsAt)}`;
  }
  if (wave.status === WAVE_STATUS.ACTIVE && wave.startsAt) {
    return `Live since ${fmt(wave.startsAt)}${wave.endsAt ? ` · closes ${fmt(wave.endsAt)}` : ''}`;
  }
  if (wave.status === WAVE_STATUS.COMPLETED && wave.startsAt) {
    return `Ran ${fmt(wave.startsAt)} → ${fmt(wave.endsAt)}`;
  }
  return 'Half-Time → Full-Time + window';
};
